import { useState } from 'react';
import { integration } from '../../integration';
import { useStore } from '../../stores/editor.store';
import { Scene3D } from '../../3D/Scene3D';
import { SceneObject } from '../../types';
import { GenerationTarget } from '../../types/game';

interface CreatorCopilotProps {
  sceneRef: React.MutableRefObject<Scene3D | null>;
}

const TARGETS: { value: GenerationTarget; label: string }[] = [
  { value: 'scene', label: 'Scene' },
  { value: 'concept', label: 'Concept' },
  { value: 'narrative', label: 'Narrative' },
  { value: 'entity', label: 'Entity' },
];

export function CreatorCopilot({ sceneRef }: CreatorCopilotProps) {
  const [prompt, setPrompt] = useState('');
  const [target, setTarget] = useState<GenerationTarget>('scene');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const [log, setLog] = useState<string[]>([]);

  const append = (line: string) => setLog((l) => [line, ...l].slice(0, 12));

  const generate = async () => {
    const p = prompt.trim();
    if (!p || busy) return;
    setBusy(true);
    append(`▸ ${target}: ${p}`);
    try {
      const game = useStore.getState().getGame();
      const identity = await integration.identity.getCurrentIdentity();
      const result = await integration.generation.generate(
        { gameId: game.id || 'local', target, prompt: p, mode: 'create' },
        identity
      );

      (result.warnings || []).forEach((w) => append(`⚠ ${w}`));

      if (target === 'scene' && Array.isArray(result.artifact)) {
        const generated = result.artifact as SceneObject[];
        const current = sceneRef.current?.getAllObjects() ?? [];
        const combined = [...current, ...generated];
        sceneRef.current?.loadObjects(combined);
        useStore.getState().loadScene(combined);
        append(`✓ added ${generated.length} object(s) to the scene`);
      } else {
        append(`✓ ${JSON.stringify(result.artifact)}`);
      }
      setPrompt('');
    } catch {
      append('✗ generation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-gray-900 border-t border-gray-700 text-white">
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Creator Copilot</span>
          <span className="text-[10px] uppercase tracking-wide text-purple-400 bg-purple-500/10 border border-purple-500/30 rounded px-1.5 py-0.5">
            AI
          </span>
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-gray-400 hover:text-white text-xs"
        >
          {open ? 'Hide ▾' : 'Show ▴'}
        </button>
      </div>

      {open && (
        <div className="px-4 pb-3 flex gap-3" style={{ height: 140 }}>
          {/* Prompt column */}
          <div className="flex flex-col gap-2 w-1/2">
            <div className="flex gap-2">
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value as GenerationTarget)}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs"
              >
                {TARGETS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <button
                onClick={generate}
                disabled={busy || !prompt.trim()}
                className="bg-purple-600 hover:bg-purple-500 disabled:opacity-40 rounded px-4 py-1 text-xs font-semibold"
              >
                {busy ? 'Generating…' : 'Generate'}
              </button>
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) generate();
              }}
              placeholder="Describe what to create — e.g. 'a small village square with a fountain'. (⌘/Ctrl+Enter to generate)"
              className="flex-1 resize-none bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Log column */}
          <div className="w-1/2 bg-gray-950 border border-gray-800 rounded p-2 overflow-y-auto text-xs font-mono text-gray-300">
            {log.length === 0 ? (
              <p className="text-gray-600">
                Results appear here. Try a Scene prompt to drop objects into the
                viewport.
              </p>
            ) : (
              log.map((line, i) => (
                <div key={i} className="whitespace-pre-wrap leading-relaxed">
                  {line}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
